import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../lib/theme';
import { useCrm } from '../lib/crm';
import { useKeycloak } from '../lib/auth';
import { authService } from '../lib/services';
import { useCurrentUser } from '../lib/currentUser';
import { useOrgName } from '../lib/useOrgName';
import { roleLabel, allowedByRoles } from '../lib/roles';
import { rolesForPath } from '../lib/apps';
import NotificationBell from './NotificationBell';

/**
 * `crumbs` accepts:
 *   - a string                  → not clickable
 *   - [label]                   → not clickable
 *   - [label, path]             → clickable Link to `path`
 *   - { label, to }             → clickable Link to `to`
 *
 * Existing callers that pass plain strings continue to work.
 */
function CrumbItem({ value, isLast }) {
  let label, to;
  if (Array.isArray(value)) {
    [label, to] = value;
  } else if (value && typeof value === 'object') {
    ({ label, to } = value);
  } else {
    label = value;
  }
  if (to && !isLast) {
    return <Link to={to} className="crumb crumb-link">{label}</Link>;
  }
  return <span className="crumb">{label}</span>;
}

/* ─── Signed-in identity ─── */

/** "Abdul Nazeer" → "Abdul N." ; single word or email left as-is. */
function shortName(name) {
  if (!name) return null;
  if (name.includes('@')) return name;
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
}

function initialsOf(name) {
  if (!name) return '?';
  const base = name.includes('@') ? name.split('@')[0] : name;
  const parts = base.trim().split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : base.slice(0, 2);
  return letters.toUpperCase();
}

/* ─── Avatar dropdown ─── */
function AvatarMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const kc = useKeycloak();
  const { user, role, loading } = useCurrentUser();
  const orgName = useOrgName();

  const fullName = user?.name || user?.email || null;
  const displayName = shortName(fullName) || (loading ? '…' : 'Signed in');
  const roleText = user?.roles?.length ? roleLabel(role) : null;
  const subtitle = [roleText, orgName].filter(Boolean).join(' · ');

  const handleSignOut = async () => {
    setOpen(false);
    // 1. Audit the logout on the backend (best-effort; non-blocking).
    try { await authService.logout(); } catch (_) { /* ignore */ }

    // 2. Wipe any local UI state so the next user lands on a clean slate.
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (_) { /* ignore */ }

    // 3. Tell Keycloak to invalidate the SSO session and redirect home.
    if (kc) {
      kc.logout({ redirectUri: window.location.origin });
    } else {
      // Local Spring profile (no Keycloak provider in play) — just reload.
      window.location.href = window.location.origin;
    }
  };

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="topbar-avatar"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={fullName ? `${fullName}${subtitle ? ` — ${subtitle}` : ''}` : 'Profile'}
        style={{ border: 'none', cursor: 'pointer' }}
      >{fullName ? initialsOf(fullName) : '…'}</button>
      {open && (
        <div role="menu" className="topbar-menu">
          <div className="topbar-menu-head">
            <div style={{ fontSize: 13, fontWeight: 600 }} title={fullName || undefined}>{displayName}</div>
            {subtitle && <div className="mono text-xs text-muted">{subtitle}</div>}
            {user?.email && user.email !== fullName && (
              <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>{user.email}</div>
            )}
          </div>
          <button className="topbar-menu-item" onClick={() => { setOpen(false); navigate('/customers'); }}>
            <span aria-hidden="true">✦</span> My customers
          </button>
          <button className="topbar-menu-item" onClick={() => { setOpen(false); navigate('/settings'); }}>
            <span aria-hidden="true">⚙</span> Settings
          </button>
          <button className="topbar-menu-item" onClick={() => { setOpen(false); navigate('/settings'); }}>
            <span aria-hidden="true">☼</span> Appearance
          </button>
          <div className="topbar-menu-sep" />
          <button className="topbar-menu-item danger" onClick={handleSignOut}>
            <span aria-hidden="true">⎋</span> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Command palette search ─── */
function CommandPalette() {
  const { customers, leads, deals } = useCrm();
  // The palette is a navigation surface like the side rail, so it obeys the
  // same table. Without this a hidden page stays one keystroke away.
  const { roles } = useCurrentUser();
  const mayOpen = useCallback(
    (path) => allowedByRoles(roles, rolesForPath(path)),
    [roles],
  );
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      } else if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) {
      return [
        ...[
          { kind: 'page', label: 'Open Customers', path: '/customers', hint: 'go' },
          { kind: 'page', label: 'Open Deals', path: '/deals', hint: 'go' },
          { kind: 'page', label: 'Open Leads', path: '/leads', hint: 'go' },
        ].filter((r) => mayOpen(r.path)),
        ...customers.slice(0, 4).map(c => ({
          kind: 'customer', label: c.name, hint: c.stage, path: `/customers/${c.id}`,
        })),
      ];
    }
    const out = [];
    for (const c of customers) {
      const hay = `${c.name} ${c.email || ''} ${c.phone || ''} ${(c.tags || []).join(' ')}`.toLowerCase();
      if (hay.includes(term)) {
        out.push({ kind: 'customer', label: c.name, hint: `${c.stage} · ${c.phone || ''}`, path: `/customers/${c.id}` });
      }
      if (out.length >= 8) break;
    }
    for (const l of leads) {
      const hay = `${l.id} ${l.vehicle} ${l.source}`.toLowerCase();
      if (hay.includes(term)) {
        out.push({ kind: 'lead', label: l.vehicle, hint: `Lead ${l.id} · ${l.source}`, path: `/leads/${l.id}` });
      }
      if (out.length >= 12) break;
    }
    for (const d of deals) {
      const hay = `${d.title} ${d.vehicle}`.toLowerCase();
      if (hay.includes(term)) {
        out.push({ kind: 'deal', label: d.title, hint: `Deal · ${d.stage}`, path: '/deals' });
      }
      if (out.length >= 14) break;
    }
    if (out.length === 0) out.push({ kind: 'empty', label: 'No matches', hint: '', path: null });
    return out;
  }, [q, customers, leads, deals, mayOpen]);

  function go(idx) {
    const item = results[idx];
    if (!item || !item.path) return;
    navigate(item.path);
    setQ('');
    setOpen(false);
    inputRef.current?.blur();
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(highlight); }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  }

  return (
    <div ref={wrapperRef} className="topbar-search-wrap">
      <div className={`topbar-search ${open ? 'focused' : ''}`}>
        <span style={{ fontSize: 14 }}>🔍</span>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setHighlight(0); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search customers, leads, deals…"
          className="topbar-search-input"
          aria-label="Global search"
        />
        <span className="topbar-search-kbd">/</span>
      </div>
      {open && (
        <div className="topbar-search-results" role="listbox">
          {results.map((r, i) => (
            <button
              key={i}
              role="option"
              aria-selected={i === highlight}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => go(i)}
              className={`topbar-search-row ${i === highlight ? 'active' : ''}`}
              disabled={r.kind === 'empty'}
            >
              <span className={`tag-kind tag-${r.kind}`}>{r.kind}</span>
              <span style={{ flex: 1, fontWeight: 500, color: 'var(--text-primary)' }}>{r.label}</span>
              <span className="mono text-xs text-muted">{r.hint}</span>
            </button>
          ))}
          <div className="topbar-search-foot mono text-xs text-muted">
            ↑↓ navigate · ↵ open · esc close · / focus
          </div>
        </div>
      )}
    </div>
  );
}

function derivedCrumbs(pathname) {
  const seg = pathname.split('/').filter(Boolean);
  if (seg.length === 0) return ['Home'];
  const map = {
    leads: 'Lead Accelerator',
    'dead-leads': 'Lost Leads',
    deals: 'Deals',
    customers: 'Customers',
    service: 'Service Advisor',
    triage: 'Triage & QA',
    compliance: 'Compliance',
    settings: 'Settings',
  };
  const out = [];
  let cur = '';
  seg.forEach((s, i) => {
    cur += '/' + s;
    const label = map[s] || s;
    if (i < seg.length - 1) out.push([label, cur]);
    else out.push(label);
  });
  return out;
}

export default function TopBar({ crumbs = [], right = null }) {
  const { mode, toggleMode } = useTheme();
  const location = useLocation();
  const effectiveCrumbs = crumbs.length ? crumbs : derivedCrumbs(location.pathname);

  return (
    <div className="topbar">
      <CommandPalette />
      <div className="topbar-breadcrumbs">
        {effectiveCrumbs.map((c, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {i > 0 && <span className="sep">›</span>}
            <CrumbItem value={c} isLast={i === effectiveCrumbs.length - 1} />
          </span>
        ))}
      </div>
      <div className="topbar-right">
        {right}
        <NotificationBell />
        <button
          type="button"
          className="topbar-theme-btn"
          onClick={toggleMode}
          title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label="Toggle color mode"
        >
          {mode === 'dark' ? '☀' : '☾'}
        </button>
        <AvatarMenu />
      </div>
    </div>
  );
}
